'use client';
import type { Placement } from '@floating-ui/react-dom';
import { computePosition, flip, offset, shift } from '@floating-ui/react-dom';
import { cx } from '@linaria/core';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

import type { Ref } from '~types/Ref';

import { DropdownElementWrapper, PopoverWrapper } from './styles';

type DropdownProps = {
	className?: string;
	children?: React.ReactNode;
	placement?: Placement;
	onChange?(showed: boolean): void;
};

const checkNode = (parent: HTMLElement, child: HTMLElement | null) => {
	let currentNode: HTMLElement | null = child;
	while (currentNode !== null) {
		if (currentNode.dataset.coc) {
			return false;
		} else if (currentNode === parent) {
			return true;
		} else {
			currentNode = currentNode.parentElement;
		}
	}
	return false;
};

export const Dropdown = ({
	children,
	className,
	placement,
	ref,
	onChange,
}: DropdownProps & Ref<HTMLElement>) => {
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const onChangeRef = useRef(onChange);

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		const target = (ref as RefObject<HTMLElement>)?.current;
		const popover = popoverRef.current;

		if (!target || !popover) {
			return;
		}

		if (!popover.showPopover) {
			target.style.display = 'none';
			popover.style.display = 'none';
			return;
		}
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		let handleTargetClick = () => {};
		const handleAnyClick = (event: MouseEvent) => {
			if (popover && !checkNode(popover, event.target as HTMLElement)) {
				popover.hidePopover();
				document.removeEventListener('click', handleAnyClick);

				onChangeRef.current?.(false);
				setTimeout(() => {
					target.addEventListener('click', handleTargetClick);
				}, 0);
			}
		};

		handleTargetClick = () => {
			if (popover) {
				popover.showPopover();
				void computePosition(target, popover, {
					placement,
					middleware: [flip(), shift({ padding: 5 }), offset(6)],
				}).then(({ x, y }) => {
					Object.assign(popover.style, {
						left: `${x}px`,
						top: `${y}px`,
					});

					target.removeEventListener('click', handleTargetClick);
					setTimeout(() => {
						document.addEventListener('click', handleAnyClick);
					}, 0);
					onChangeRef.current?.(true);
				});
			}
		};

		target.addEventListener('click', handleTargetClick);

		return () => {
			target.removeEventListener('click', handleTargetClick);
			document.removeEventListener('click', handleAnyClick);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			ref={popoverRef}
			className={cx(PopoverWrapper, className)}
			popover="manual"
			role="menu"
		>
			{children}
		</div>
	);
};

type DropdownElementProps = {
	closeOnClick?: boolean;
	className?: string;
};

export const DropdownElement = ({
	className,
	closeOnClick = true,
	...restProps
}: DropdownElementProps & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
	<button
		data-coc={closeOnClick ? true : undefined}
		className={cx(DropdownElementWrapper, className)}
		role="menuitem"
		{...restProps}
	/>
);
